import { ReactComponent as UploadIcon } from 'assets/icons/icon-cloud-upload.svg';
import { ReactComponent as MoneyIcon } from 'assets/icons/icon-money.svg';
import Button from 'components/Button';
import Card from 'components/Card/Card';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import InlineTextEdit, {
  useInlineTextEditState,
} from 'components/InlineTextEdit/InlineTextEdit';
import JcapPagination from 'components/JcapPagination/JcapPagination';
import Page from 'components/Page';
import SearchInput from 'components/SearchInput';
import Table from 'components/Table/Table';
import { useUpdateActualBalanceMutation } from 'components/ViewBankAccount/UpdateActualBalance.generated';
import { curr } from 'helpers/curr';
import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { routeMap, useNavigate } from 'routeMap';
import {
  useConfirm,
  usePagination,
  useTextInput,
  useWrappedUrqlMutation,
} from 'toolbox';
import Flex from 'toolbox/src/components/Flex/Flex';
import HeroIconTrash from 'toolbox/src/components/HeroIcons/HeroIconTrash';
import Stack from 'toolbox/src/components/Stack/Stack';
import {
  useAdminGetActualBalancesQuery,
  AdminGetActualBalancesQueryVariables,
} from './AdminGetActualBalances.generated';
import { useDeleteActualBalanceMutation } from './DeleteActualBalance.generated';
import dayjs from 'dayjs';
import { useSortedTable } from 'toolbox/src/hooks/useSortedTable';
import HeroIconExternalLink from 'toolbox/src/components/HeroIcons/HeroIconExternalLink';

interface ViewBalancesProps {}

export const ViewBalances: React.FC<ViewBalancesProps> = ({ children }) => {
  const { sortingState, orderBy, onColumnClick } = useSortedTable<
    AdminGetActualBalancesQueryVariables['orderBy']
  >({
    defaultOrderBy: 'date',
    mapping: {
      date: (o) => ({
        date: o,
      }),
      Org: (o) => ({
        bank_account: {
          currency_book: {
            business_unit: {
              organisation: {
                name: o,
              },
            },
          },
        },
      }),
      Balance: (o) => ({
        balance: o,
      }),
      'Interest Rate': (o) => ({
        interest_rate: o,
      }),
      maturity_date: (o) => ({
        maturity_date: o,
      }),
      'Date Created': (o) => ({
        created_at: o,
      }),
    },
  });
  const pagination = usePagination({ resultsPerPage: 10 });

  const filterInput = useTextInput({
    onThrottledInputChange: () => {
      pagination.resetPage();
    },
  });

  const [balancesResult] = useAdminGetActualBalancesQuery({
    variables: {
      limit: pagination.limit,
      offset: pagination.offset,
      search: `%${filterInput.throttledValue || ''}%`,
      orderBy,
    },
  });

  pagination.useWatchAllResultsCount(
    balancesResult.data?.actual_balances_aggregate.aggregate.count,
  );

  const {
    setWhichInputIdIsBeingEdited,
    whichInputIdIsBeingEdited,
    closeAllInputs,
  } = useInlineTextEditState();

  const updateActualBalance = useWrappedUrqlMutation(
    useUpdateActualBalanceMutation,
    {
      onSuccess: () => {
        toast('Updated account balance successfully');
        closeAllInputs();
      },
      onError: () => {
        toast.error('Something went wrong. Could not update account balance.');
      },
    },
  );

  const deleteBalance = useWrappedUrqlMutation(useDeleteActualBalanceMutation, {
    onSuccess: () => {
      toast('Deleted account balance successfully');
    },
    onError: () => {
      toast.error('Something went wrong. Could not delete account balance.');
    },
  });

  const deleteBalanceConfirmation = useConfirm();

  const navigate = useNavigate();

  return (
    <Page
      icon={<MoneyIcon className="inline w-10 h-10 mr-3 -mb-2 text-gray-500" />}
      title="View Balances"
      cardWrapperClassName="max-w-full"
    >
      <Card title="Balances">
        <Stack>
          <Flex justifyContent="space-between" alignItems="center">
            <div className="max-w-xs -mb-4">
              <SearchInput
                placeholder="Search..."
                {...filterInput.inputProps}
              />
            </div>
            <div>
              <Button onClick={() => navigate.adminUploadBalances()}>
                <UploadIcon className="flex-shrink-0 inline w-5 h-5 mr-2" />
                Upload New Balances
              </Button>
            </div>
          </Flex>
          <Table
            sortingState={sortingState}
            onColumnClick={onColumnClick}
            data={balancesResult.data?.actual_balances || []}
            gridTemplateColumns={'1.66fr 0.8fr repeat(5, 1fr) 50px'}
            columns={[
              {
                Header: 'Account',
                accessor: ({ bank_account }) => (
                  <div className="flex flex-col">
                    <div>
                      <Link
                        to={routeMap.viewBankAccountById({
                          params: {
                            account_id: bank_account.account_id,
                          },
                        })}
                        className="font-semibold text-jcap-600 hover:underline"
                      >
                        {bank_account.account_id}
                      </Link>
                      <HeroIconExternalLink className="inline w-3 h-3 ml-1 -mt-px text-gray-500" />
                    </div>
                    <div>
                      <Link
                        to={routeMap.viewBankById({
                          params: {
                            id: bank_account?.bank?.id,
                          },
                        })}
                        className="text-xs truncate text-jcap-500 hover:underline"
                      >
                        {bank_account?.bank?.name}
                      </Link>
                      <HeroIconExternalLink className="inline w-3 h-3 mb-px ml-1 text-gray-500" />
                    </div>
                  </div>
                ),
              },
              {
                Header: 'Balance Date',
                accessor: 'date',
              },
              {
                Header: 'Balance',
                accessor: ({ balance, balance_gbp, date, bank_account }) => {
                  const baseCurrency = bank_account?.currency.prefix;
                  return (
                    <div className="flex flex-col">
                      <p className="font-semibold">
                        {baseCurrency} {curr(balance)}
                      </p>
                    </div>
                  );
                },
              },
              {
                Header: 'Equivalent Balance',
                accessor: ({
                  balance,
                  balance_gbp,
                  balance_eur,
                  balance_usd,
                  bank_account,
                }) => {
                  const baseCurrency = bank_account?.currency.prefix;
                  return (
                    <div className="text-xs">
                      {baseCurrency !== 'GBP' && (
                        <p className="text-gray-600">
                          GBP{' '}
                          {balance_gbp === null
                            ? 'NULL'
                            : curr(balance <= 0 ? 0 : balance_gbp)}
                        </p>
                      )}
                      {baseCurrency !== 'USD' && (
                        <p className="text-gray-600">
                          USD{' '}
                          {balance_usd === null
                            ? 'NULL'
                            : curr(balance <= 0 ? 0 : balance_usd)}
                        </p>
                      )}
                      {baseCurrency !== 'EUR' && (
                        <p className="text-gray-600">
                          EUR{' '}
                          {balance_eur === null
                            ? 'NULL'
                            : curr(balance <= 0 ? 0 : balance_eur)}
                        </p>
                      )}
                    </div>
                  );
                },
              },
              {
                Header: 'Interest Rate',
                accessor: ({ interest_rate, id }) => (
                  <InlineTextEdit
                    label="Interest Rate"
                    setWhichInputIdIsBeingEdited={setWhichInputIdIsBeingEdited}
                    whichInputIdIsBeingEdited={whichInputIdIsBeingEdited}
                    value={interest_rate}
                    onSubmit={({ value }) =>
                      updateActualBalance.dispatch({
                        id,
                        interest_rate: Number(value),
                      })
                    }
                    isLoading={updateActualBalance.fetching}
                  >
                    {interest_rate}%
                  </InlineTextEdit>
                ),
              },
              {
                id: 'maturity_date',
                Header: 'Type',
                accessor: ({ maturity_date, bank_account }) => {
                  const dateDiff = dayjs(maturity_date).diff(dayjs(), 'day');
                  return (
                    <div>
                      {bank_account.product_type === 'NOTICE' ? (
                        <div>
                          <p>{bank_account.notice_days}D NOTICE</p>
                          <p className="text-xs text-gray-600">
                            Matures{' '}
                            {maturity_date || bank_account.fixed_maturity_date}
                            <br />
                            {dateDiff >= 0 && dateDiff + ' days to Maturity'}
                          </p>
                        </div>
                      ) : bank_account.product_type === 'FIXED' ? (
                        <div>
                          <p>FIXED</p>

                          <p className="text-xs text-gray-600">
                            Matures{' '}
                            {maturity_date || bank_account.fixed_maturity_date}
                            <br />
                            {dateDiff >= 0 && dateDiff + ' days to Maturity'}
                          </p>
                        </div>
                      ) : (
                        <div>{bank_account.product_type}</div>
                      )}
                    </div>
                  );
                },
              },
              {
                Header: 'Date Imported',
                accessor: ({ created_at }) =>
                  dayjs(created_at).format('YYYY-MM-DD'),
              },
              {
                id: 'actions',
                accessor: ({ id }) => {
                  return (
                    <button
                      aria-label="Delete Balance"
                      className="px-2 py-1 bg-gray-100 focus:outline-none focus:bg-gray-200"
                      onClick={() => {
                        // in case the original id gets stale after a render
                        const immutableId = id;
                        deleteBalanceConfirmation.confirm(() => {
                          deleteBalance.dispatch({
                            id: immutableId,
                          });
                        });
                      }}
                    >
                      <HeroIconTrash className="w-6 h-5 text-gray-600" />
                    </button>
                  );
                },
              },
            ]}
          />
          <JcapPagination {...pagination} />
        </Stack>
      </Card>
      <ConfirmationModal
        {...deleteBalanceConfirmation}
        title="Are you sure you want to delete this balance?"
        description="This action cannot be reversed."
        confirmText="Delete"
      />
    </Page>
  );
};

export default ViewBalances;
